import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { SERVICES, PHONE_DISPLAY, EMAIL } from "../data/content";
import { Logo } from "./Navbar";

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ThreadsIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-5.985-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.135 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.086.88-.76 2.119-1.188 3.583-1.24.784-.026 2.042.168 2.96.505-.024-1.24-.34-5.22-3.093-5.213-1.18.005-2.03.62-2.547 1.844l-2.156-.63c.447-1.797 1.65-3.192 3.673-3.713.347-.09.865-.155 1.662-.156h.62c1.995 0 3.333.84 4.183 2.625.627 1.324.84 3.032.84 4.516l.007.016c.87.56 1.598 1.352 2.085 2.293.667 1.285.797 3.007.172 4.65-1.214 3.198-4.285 4.832-8.378 4.832zm-1.663-8.56c-1.078.064-1.584.367-1.907.7-.378.39-.545.882-.51 1.517.035.627.354 1.112.947 1.44.286.158.837.216 1.35.187 1.067-.067 1.875-.484 2.326-1.226.29-.484.51-1.176.621-2.114-.453-.396-1.4-.563-2.827-.505z" />
  </svg>
);

export const Footer = () => (
  <footer className="bg-aubergine-deep text-cream relative overflow-hidden">
    <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-cream/10 spin-slower" style={{ borderStyle: "dashed" }} />
    <div className="absolute -bottom-40 -left-24 w-[28rem] h-[28rem] rounded-full bg-aubergine-light/30 blur-3xl" />
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 pt-20 pb-10 relative">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <Logo dark />
          <p className="mt-5 text-sm leading-relaxed text-cream/70">
            A complete hospitality growth and creative partner for restaurants, pubs, cafés, hotels & resorts — marketing, content, launch and branding under one roof.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { Icon: Instagram, id: "instagram", href: "https://www.instagram.com/growmyrestrooffical/" },
              { Icon: Youtube, id: "youtube", href: "https://www.youtube.com/@GrowMyRestro" },
              { Icon: Facebook, id: "facebook", href: "https://www.facebook.com/growmyrestro?ref=1" },
              { Icon: Linkedin, id: "linkedin", href: "https://www.linkedin.com/in/growmyrestro-offical-4587493a9/" },
              { Icon: ThreadsIcon, id: "threads", href: "https://www.threads.com/@growmyrestrooffical?hl=en" },
              { Icon: XIcon, id: "x", href: "https://x.com/Growmyrestro" },
            ].map(({ Icon, id, href }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`GrowMyRestro on ${id}`}
                data-testid={`footer-social-${id}`}
                className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-cream/70 hover:bg-saffron hover:text-aubergine-deep hover:border-saffron transition-all duration-300"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-mono uppercase text-xs tracking-[0.22em] text-saffron-warm mb-5">Quick Links</h4>
          <ul className="space-y-3 text-sm">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About Us" },
              { to: "/services", label: "Services" },
              { to: "/industries", label: "Industries" },
              { to: "/work", label: "Our Work" },
              { to: "/blog", label: "Blog" },
              { to: "/contact", label: "Contact Us" },
            ].map((l) => (
              <li key={l.to + l.label}>
                <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-cream/70 hover:text-saffron-warm transition-colors inline-flex items-center gap-1.5 group">
                  {l.label}
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-mono uppercase text-xs tracking-[0.22em] text-saffron-warm mb-5">Our Services</h4>
          <ul className="space-y-3 text-sm">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to={s.slug} data-testid={`footer-service-${s.id}`} className="text-cream/70 hover:text-saffron-warm transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-mono uppercase text-xs tracking-[0.22em] text-saffron-warm mb-5">Contact Us</h4>
          <ul className="space-y-4 text-sm text-cream/70">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-saffron-warm shrink-0" />
              <a href="https://wa.me/918105032380" data-testid="footer-phone-link" className="hover:text-saffron-warm transition-colors">{PHONE_DISPLAY} (WhatsApp)</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-saffron-warm shrink-0" />
              <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="hover:text-saffron-warm transition-colors">{EMAIL}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-saffron-warm shrink-0" />
              <span>Bengaluru, Karnataka, India</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-0.5 text-saffron-warm shrink-0" />
              <span>Mon – Sat: 9:30 AM – 7:30 PM IST</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-cream/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-cream/50">
        <span>© 2026 GrowMyRestro. All rights reserved.</span>
        <span className="font-serif italic text-sm text-cream/60">"We don't just help you get noticed. We help you grow."</span>
      </div>
    </div>
  </footer>
);
